import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage, LANGUAGES } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard,
  Receipt,
  FolderOpen,
  RefreshCw,
  Bell,
  Users,
  LogOut,
  ChevronRight,
  Menu,
  X,
  FileCheck,
  FileText,
  Globe,
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_9ab9ec17-6f7e-424d-a923-36914df0b611/artifacts/t65s39z6_rt.png";

const NAV_KEYS = [
  { path: "/", key: "nav.dashboard", icon: LayoutDashboard },
  { path: "/expenses", key: "nav.expenses", icon: Receipt },
  { path: "/review", key: "nav.review", icon: FileCheck },
  { path: "/reports", key: "nav.reports", icon: FileText },
  { path: "/categories", key: "nav.categories", icon: FolderOpen },
  { path: "/recurring", key: "nav.recurring", icon: RefreshCw },
  { path: "/reminders", key: "nav.reminders", icon: Bell },
];

const ADMIN_KEYS = [
  { path: "/users", key: "nav.users", icon: Users },
];

const NavContent = ({ user, onNavigate, t }) => (
  <>
    <nav className="px-3 space-y-1">
      {NAV_KEYS.map((item) => (
        <NavLink
          key={item.path}
          to={item.path}
          end={item.path === "/"}
          onClick={onNavigate}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-md font-medium transition-colors ${
              isActive
                ? "bg-[#E31E24]/10 text-[#E31E24] border-r-2 border-[#E31E24]"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
            }`
          }
          data-testid={`nav-${item.path.replace("/", "") || "dashboard"}`}
        >
          <item.icon className="h-5 w-5" />
          <span>{t(item.key)}</span>
          <ChevronRight className="h-4 w-4 ml-auto opacity-50" />
        </NavLink>
      ))}

      {user?.is_admin && (
        <>
          <div className="my-4 px-3">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Administration
            </div>
          </div>
          {ADMIN_KEYS.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={onNavigate}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md font-medium transition-colors ${
                  isActive
                    ? "bg-[#E31E24]/10 text-[#E31E24] border-r-2 border-[#E31E24]"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`
              }
              data-testid={`nav-${item.path.replace("/", "")}`}
            >
              <item.icon className="h-5 w-5" />
              <span>{t(item.key)}</span>
              <ChevronRight className="h-4 w-4 ml-auto opacity-50" />
            </NavLink>
          ))}
        </>
      )}
    </nav>
  </>
);

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { t, language, changeLanguage, languages } = useLanguage();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const LanguageSelector = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2">
          <Globe className="h-4 w-4" />
          <span>{languages[language].flag}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {Object.values(languages).map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            className={language === lang.code ? "bg-muted" : ""}
          >
            <span className="mr-2">{lang.flag}</span>
            {lang.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-[#fafafa]">
      {/* Mobile Header */}
      <header className="md:hidden sticky top-0 z-50 bg-white border-b border-border px-4 py-3 flex items-center justify-between">
        <img 
          src={LOGO_URL} 
          alt="Kaufmanns Consulting" 
          className="h-8 object-contain"
          data-testid="logo-mobile"
        />
        <div className="flex items-center gap-2">
          <LanguageSelector />
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[280px] p-0">
              <div className="flex flex-col h-full">
                {/* Mobile Logo */}
                <div className="p-4 border-b border-border">
                  <img 
                    src={LOGO_URL} 
                    alt="Kaufmanns Consulting" 
                    className="h-8 object-contain"
                  />
                </div>
                
                {/* Mobile Navigation */}
                <ScrollArea className="flex-1 py-4">
                  <NavContent user={user} onNavigate={closeMobileMenu} t={t} />
                </ScrollArea>

                {/* Mobile User section */}
                <div className="p-4 border-t border-border">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="h-10 w-10 rounded-full bg-[#E31E24]/10 flex items-center justify-center">
                      <span className="text-[#E31E24] font-semibold">
                        {user?.name?.charAt(0)?.toUpperCase() || "U"}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{user?.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2"
                    onClick={handleLogout}
                    data-testid="logout-btn-mobile"
                  >
                    <LogOut className="h-4 w-4" />
                    {t("nav.logout")}
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-64 bg-white border-r border-border flex-col flex-shrink-0">
        {/* Logo */}
        <div className="p-6 border-b border-border">
          <div className="bg-white rounded-lg p-3 flex items-center justify-center">
            <img 
              src={LOGO_URL} 
              alt="Kaufmanns Consulting" 
              className="h-8 object-contain"
              data-testid="logo"
            />
          </div>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <NavContent user={user} onNavigate={() => {}} t={t} />
        </ScrollArea>

        {/* User section */}
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 rounded-full bg-[#E31E24]/10 flex items-center justify-center">
              <span className="text-[#E31E24] font-semibold">
                {user?.name?.charAt(0)?.toUpperCase() || "U"}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="outline"
            className="w-full justify-start gap-2"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4" />
            {t("nav.logout")}
          </Button>
        </div>
      </aside>

      {/* Language Selector for Desktop - in header area */}
      <div className="hidden md:flex fixed top-4 right-4 z-50">
        <LanguageSelector />
      </div>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <div className="p-4 md:p-8 max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
